import type { BulkGroupAction, IGroup } from '@hit-game/shared/types';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';

import { Modal } from '../common/Modal';
import { GAME_BADGES } from '../group/group.constants';

export interface SkippedGroup {
  group: IGroup;
  reason: string;
}

interface BulkActionSheetProps {
  open: boolean;
  action: BulkGroupAction;
  /** Nhóm ĐỦ điều kiện — sẽ chạy. */
  run: IGroup[];
  /** Nhóm bị bỏ qua kèm lý do. */
  skip: SkippedGroup[];
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const VERB: Record<BulkGroupAction, string> = { start: 'Start', stop: 'Stop' };

/**
 * Bảng xác nhận trước khi chạy bulk: chia rõ "sẽ chạy" và "bỏ qua (kèm lý do)".
 *
 * Bỏ qua là CẢNH BÁO, không phải lỗi — nhóm không đủ điều kiện đơn giản là không
 * bị đụng tới. Nút chạy tự tắt khi không có nhóm nào đủ điều kiện.
 */
export function BulkActionSheet({
  open,
  action,
  run,
  skip,
  busy,
  onConfirm,
  onClose,
}: BulkActionSheetProps) {
  const verb = VERB[action];

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={busy}
      title={`${verb} ${String(run.length + skip.length)} nhóm đã chọn`}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-ink-dim hover:bg-panel-hover hover:text-ink rounded-lg px-4 py-2 text-[13px] font-semibold transition-colors disabled:opacity-50"
          >
            Huỷ
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || run.length === 0}
            className="bg-accent flex items-center gap-2 rounded-lg px-4 py-2 text-[13px] font-semibold text-white transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {run.length > 0 ? `${verb} ${String(run.length)} nhóm` : verb}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <section className="space-y-1.5">
          <h4 className="text-success-bright flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide">
            <CheckCircle2 className="h-4 w-4" /> Sẽ {verb.toLowerCase()}
            <span className="ml-auto font-mono">{run.length}</span>
          </h4>
          {run.length > 0 ? (
            <ul className="space-y-1">
              {run.map((group) => (
                <GroupRow key={String(group._id)} group={group} tone="ok" trailing="sẵn sàng" />
              ))}
            </ul>
          ) : (
            <div className="bg-warning/10 border-warning/30 text-warning flex items-start gap-2 rounded-xl border p-3 text-[12px]">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                Không có nhóm nào đủ điều kiện {verb.toLowerCase()}. Chọn nhóm khác hoặc đổi hành
                động.
              </span>
            </div>
          )}
        </section>

        {skip.length > 0 && (
          <section className="space-y-1.5">
            <h4 className="text-warning flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide">
              <AlertTriangle className="h-4 w-4" /> Bỏ qua
              <span className="ml-auto font-mono">{skip.length}</span>
            </h4>
            <ul className="space-y-1">
              {skip.map(({ group, reason }) => (
                <GroupRow key={String(group._id)} group={group} tone="skip" trailing={reason} />
              ))}
            </ul>
          </section>
        )}
      </div>
    </Modal>
  );
}

function GroupRow({
  group,
  tone,
  trailing,
}: {
  group: IGroup;
  tone: 'ok' | 'skip';
  trailing: string;
}) {
  const toneClass = tone === 'ok' ? 'bg-success/10' : 'bg-warning/10';
  const trailClass = tone === 'ok' ? 'text-success-bright' : 'text-warning';

  return (
    <li className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] ${toneClass}`}>
      <span className="text-ink font-semibold">{group.name}</span>
      <span
        className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
          GAME_BADGES[group.gameType] ?? 'bg-panel-hover text-ink-dim'
        }`}
      >
        {group.gameType}
      </span>
      <span className={`ml-auto text-[12px] ${trailClass}`}>{trailing}</span>
    </li>
  );
}
